/**
 * Warehouse Capacity Calculator — pure logic.
 *
 * Ported from OLD Prism (`audiotrader/prism`, /warehouse) and made testable.
 * No I/O, no DOM. Consumed by components/admin/WarehouseCapacityCalculator.jsx
 * and persisted per-user via /api/warehouse-capacity/scenarios.
 *
 * Spec: docs/specs_20260602_warehouse_capacity_calculator.md
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Coerce any input to a finite, non-negative number (defaults to 0).
function num(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** The ported OLD-Prism default input set (params_json schema v1). */
export function defaultParams() {
  return {
    version: 1,
    f1aLong: { width: 2144, height: 1944, depth: 1233, levels: 4, bays: 0, itemsPerLevel: 5 },
    f1b: { width: 2144, height: 1944, depth: 624, levels: 4, bays: 0, itemsPerLevel: 5 },
    floor2: {
      width: 2144, height: 2492, depth: 624, levels: 5, bays: 140,
      totesPerLevel: 2, itemsPerTote: 13,
      toteSmall: { l: 60, w: 40, h: 20 }, // reference only — not used in the maths
      toteMedium: { l: 60, w: 40, h: 30 }, // reference only — not used in the maths
    },
    currentSqFt: 3197,
    projections: [
      { label: 'Current', items: 1000 },
      { label: 'Dec 2026', items: 5695 },
      { label: 'Dec 2027', items: 15727 },
      { label: 'Dec 2028', items: 34704 },
      { label: 'Dec 2029', items: 53611 },
    ],
  }
}

/** Derive every capacity figure from the input params. */
export function calculateCapacity(params) {
  const a = params.f1aLong || {}
  const b = params.f1b || {}
  const f2 = params.floor2 || {}

  const f1aCapacity = num(a.bays) * num(a.levels) * num(a.itemsPerLevel)
  const f1bCapacity = num(b.bays) * num(b.levels) * num(b.itemsPerLevel)
  const floor1Capacity = f1aCapacity + f1bCapacity

  const totalTotesNeeded = num(f2.bays) * num(f2.levels) * num(f2.totesPerLevel)
  const floor2Capacity = totalTotesNeeded * num(f2.itemsPerTote)

  const totalCapacity = floor1Capacity + floor2Capacity
  const sqFt = num(params.currentSqFt) || 1 // guard divide-by-zero (matches original `|| 1`)
  const itemsPerSqFt = totalCapacity / sqFt

  return { f1aCapacity, f1bCapacity, floor1Capacity, totalTotesNeeded, floor2Capacity, totalCapacity, itemsPerSqFt }
}

/** Build per-period rows for the capacity timeline bar chart. */
export function buildTimeline(projections, totalCapacity) {
  return (projections || []).map((p) => {
    const items = num(p.items)
    const utilization = totalCapacity > 0 ? (items / totalCapacity) * 100 : 0
    return {
      label: p.label,
      items,
      utilization,
      fits: items <= totalCapacity,
      barWidth: Math.min(utilization, 100),
    }
  })
}

// "Dec 2028" → "Apr 2028"; "Phase 2" (no year) → "Apr Phase 2".
function formatReached(month, label) {
  const yearMatch = String(label).match(/\b(\d{4})\b/)
  return yearMatch ? `${month} ${yearMatch[1]}` : `${month} ${label}`
}

/**
 * The period (month + year) at which projected stock crosses total capacity.
 * Linear monthly interpolation within the year that first exceeds capacity.
 * Returns null for empty input; "Beyond <last>" if never reached;
 * "Before <first>" if already over at the first row.
 */
export function findCapacityReached(projections, totalCapacity) {
  const rows = projections || []
  if (rows.length === 0) return null

  for (let idx = 1; idx < rows.length; idx++) {
    if (num(rows[idx].items) <= totalCapacity) continue

    const prevItems = num(rows[idx - 1].items)
    const monthlyGrowth = (num(rows[idx].items) - prevItems) / 12
    const itemsNeeded = totalCapacity - prevItems
    const monthsToCapacity = monthlyGrowth > 0 ? itemsNeeded / monthlyGrowth : 0
    const monthIndex = Math.min(Math.max(Math.floor(monthsToCapacity), 0), 11)
    return formatReached(MONTHS[monthIndex], rows[idx].label)
  }

  const last = rows[rows.length - 1]
  if (num(last.items) <= totalCapacity) return `Beyond ${last.label}`
  return `Before ${rows[0].label}`
}

/** Floor space required to hold the end-of-plan stock at the current density. */
export function calculateSpaceNeeded(projections, itemsPerSqFt, currentSqFt) {
  const rows = projections || []
  const endItems = rows.length ? num(rows[rows.length - 1].items) : 0
  const sqFtNeeded = itemsPerSqFt > 0 ? Math.ceil(endItems / itemsPerSqFt) : 0
  const sqFt = num(currentSqFt)
  return {
    endItems,
    sqFtNeeded,
    additionalSqFt: sqFtNeeded - sqFt,
    multiple: sqFt > 0 ? sqFtNeeded / sqFt : 0,
  }
}
